import React, { useEffect, useState } from "react"
import { AdminUser } from "../../models/user"
import { useForm } from "@mantine/form"
import { updateUser } from "../api"
import { notifications } from "@mantine/notifications"
import { BaseModal, requiredMark, requireLabelStyles } from "./BaseModal"
import { Box, Button, Group, ScrollArea, Stack, TextInput } from "@mantine/core"
import { confirmDiscardChanges } from "../utils/confirm"

const EMAIL_REGEXP = /^[A-Za-z0-9]{1}[A-Za-z0-9_.+-]*@{1}[A-Za-z0-9_.-]{1,}\.[A-Za-z0-9]{1,}$/

interface Props {
  opened: boolean
  onClose: () => void
  clientId: number
  user: AdminUser
  onSaved?: () => void
}

interface FormValues {
  email: string
}

const defaultValues: FormValues = {
  email: '',
}

export const UserEmailUpdateModal: React.FC<Props> = ({ opened, onClose, clientId, user, onSaved }) => {
  const [submitting, setSubmitting] = useState(false)

  const form = useForm<FormValues>({
    initialValues: defaultValues,
    validate: {
      email: (v) => {
        if (!v) return '必須です'
        if (!EMAIL_REGEXP.test(v)) return '正しいメールアドレスを入力してください'
        return null
      }
    },
  })

  useEffect(() => {
    if (opened) {
      form.setValues({ email: user.email })
      form.resetDirty()
    }
  }, [opened, user])

  const handleClose = () => {
    if (form.isDirty() && !confirmDiscardChanges()) {
      return
    }
    onClose()
  }

  const handleSubmit = async (values: FormValues) => {
    setSubmitting(true)

    try {
      await updateUser(clientId, user.id, { email: values.email })
      notifications.show({ message: 'メールアドレスを更新しました', color: 'teal' })
      onSaved?.()
      onClose()
    } catch (errors: any) {
      const message = Array.isArray(errors) ? errors.join('\n') : 'メールアドレスの更新に失敗しました'
      notifications.show({ message, color: 'red' })
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <BaseModal
      opened={opened}
      onClose={handleClose}
      title="メールアドレス変更"
      onExited={() => {
        form.setValues(defaultValues)
        form.resetDirty()
      }}
    >
      <form
        onSubmit={form.onSubmit(handleSubmit)}
        style={{
          display: 'flex',
          flexDirection: 'column',
          flex: 1,
          minHeight: 0,
        }}
      >
        <ScrollArea style={{ flex: 1, minHeight: 0 }} bg="#00000008" px={24}>
          <Stack gap="md" p="md">
            <TextInput
              label={<><span>メールアドレス</span>{requiredMark}</>}
              {...form.getInputProps('email')}
              styles={requireLabelStyles}
            />
          </Stack>
        </ScrollArea>

        <Box
          p="md"
          style={{
            borderTop: '1px solid #0000001A',
            backgroundColor: '#fff',
            flexShrink: 0,
          }}
        >
          <Group justify="flex-end" gap={8}>
            <Button
              variant="outline"
              radius="xl"
              color="gray"
              onClick={handleClose}
              disabled={submitting}
            >
              キャンセル
            </Button>
            <Button
              variant="filled"
              radius="xl"
              type="submit"
              loading={submitting}
            >
              更新する
            </Button>
          </Group>
        </Box>
      </form>
    </BaseModal>
  )
}
